import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { tokens } from '../../../global/theme/tokens';
import {
  formatPercent,
  secondsToHours,
  teamSummary,
} from './workTimeReportUtils';

const StatBlock = ({ label, value, trend, colors }) => {
  return (
    <Box className="flex flex-col gap-0.5 min-w-[120px]">
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <Box className="flex items-end gap-2">
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {value}
        </Typography>
        {trend && (
          <Box className="flex items-center" sx={{ color: trend.color, mb: 0.3 }}>
            {trend.up ? (
              <TrendingUpIcon sx={{ fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16 }} />
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.3 }}>
              {trend.label}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const computeTrend = (current, previous, colors) => {
  if (previous === null || previous === undefined) return null;
  if (current === null || current === undefined) return null;
  const delta = current - previous;
  if (delta === 0) return null;
  const up = delta > 0;
  return {
    up,
    color: up ? colors.success : colors.error,
    label: `${up ? '+' : ''}${delta}`,
  };
};

const computeHoursTrend = (currentSeconds, previousSeconds, colors) => {
  if (
    previousSeconds === null ||
    previousSeconds === undefined ||
    previousSeconds === 0
  )
    return null;
  const delta = currentSeconds - previousSeconds;
  if (delta === 0) return null;
  const up = delta > 0;
  return {
    up,
    color: up ? colors.success : colors.error,
    label: `${up ? '+' : ''}${secondsToHours(delta, 0)} h`,
  };
};

const WorkTimeReportBanner = ({ entries, previousEntries }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const current = teamSummary(entries);
  const previous = previousEntries ? teamSummary(previousEntries) : null;

  return (
    <Box
      className="rounded-lg backdrop-blur-lg p-3 flex flex-row flex-wrap gap-4 items-center justify-around"
      sx={{ bgcolor: colors.glass }}
    >
      <StatBlock
        label={t('Team total')}
        value={`${secondsToHours(current.teamWorkedSeconds, 0)} h`}
        trend={
          previous
            ? computeHoursTrend(
                current.teamWorkedSeconds,
                previous.teamWorkedSeconds,
                colors,
              )
            : null
        }
        colors={colors}
      />
      <StatBlock
        label={t('Average effectivity')}
        value={formatPercent(current.avgEffectivity)}
        trend={
          previous
            ? computeTrend(current.avgEffectivity, previous.avgEffectivity, colors)
            : null
        }
        colors={colors}
      />
      <StatBlock
        label={t('Average target achievement')}
        value={formatPercent(current.avgTargetAchievement)}
        trend={
          previous
            ? computeTrend(
                current.avgTargetAchievement,
                previous.avgTargetAchievement,
                colors,
              )
            : null
        }
        colors={colors}
      />
      <StatBlock
        label={t('Average absence rate')}
        value={formatPercent(current.avgAbsenceRate)}
        trend={
          previous
            ? computeTrend(current.avgAbsenceRate, previous.avgAbsenceRate, colors)
            : null
        }
        colors={colors}
      />
      <StatBlock
        label={t('Billable hours')}
        value={`${secondsToHours(current.teamBillableSeconds, 0)} h`}
        trend={
          previous
            ? computeHoursTrend(
                current.teamBillableSeconds,
                previous.teamBillableSeconds,
                colors,
              )
            : null
        }
        colors={colors}
      />
      <StatBlock
        label={t('Active employees')}
        value={current.activeEmployees}
        colors={colors}
      />
    </Box>
  );
};

export default WorkTimeReportBanner;
